import { HttpErrorResponse } from '@angular/common/http';
import { IssueModel, LeaseConflictData, MetaModel } from '@fleet/model';

/** User-facing fallback when API issues are absent or malformed; exported for parity checks elsewhere. */
export const FLEET_GENERIC_API_SUPPORT_ISSUE_MESSAGE =
  'An error has occurred. Please try again, or contact support for further assistance.';

function isConflictDataWrapperType(metaType: string | undefined): boolean {
  return typeof metaType === 'string' && metaType.toUpperCase() === 'CONFLICT';
}

/** Single row, or batch of rows, inside `MetaModel.data` or `ApiResponse.data`. */
function asRowCandidates(payload: unknown): unknown[] {
  if (payload === null || payload === undefined) {
    return [];
  }
  return Array.isArray(payload) ? payload : [payload];
}

/** Row `type` matches `conflictType` (case-insensitive); missing/blank `type` counts as a match. */
function rowMatchesConflictType(d: unknown, conflictType: string): boolean {
  if (typeof d !== 'object' || d === null) {
    return false;
  }
  const t = (d as { type?: unknown }).type;
  if (t == null) {
    return true;
  }
  if (typeof t === 'string' && t.trim() === '') {
    return true;
  }
  return String(t).toLowerCase() === conflictType.toLowerCase();
}

function normalizeLeaseConflictRow(row: LeaseConflictData): LeaseConflictData {
  const id = row.lease?.leaseId;
  if (typeof id === 'number' && !Number.isNaN(id)) {
    return {
      ...row,
      lease: { ...row.lease, leaseId: String(id) },
    };
  }
  return row;
}

function appendAllConflictDataRows(target: unknown[], chunks: unknown[]): void {
  for (const d of chunks) {
    if (typeof d !== 'object' || d === null) {
      continue;
    }
    if (isLeaseConflictData(d)) {
      target.push(normalizeLeaseConflictRow(d as LeaseConflictData));
    } else {
      target.push(d);
    }
  }
}

function appendConflictDataRows(
  target: unknown[],
  chunks: unknown[],
  conflictType: string
): void {
  const kind = conflictType.toLowerCase();
  for (const d of chunks) {
    if (!rowMatchesConflictType(d, conflictType)) {
      continue;
    }
    if (kind === 'lease') {
      if (!isLeaseConflictData(d)) {
        continue;
      }
      target.push(normalizeLeaseConflictRow(d as LeaseConflictData));
    } else {
      target.push(d);
    }
  }
}

export function isGenericFleetSupportIssue(issue: IssueModel): boolean {
  return issue?.message === FLEET_GENERIC_API_SUPPORT_ISSUE_MESSAGE;
}

function isLeaseConflictData(value: unknown): value is LeaseConflictData {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const lease = (value as { lease?: { leaseId?: unknown } }).lease;
  if (typeof lease !== 'object' || lease === null) {
    return false;
  }
  const leaseId = lease.leaseId;
  if (typeof leaseId === 'string') {
    return leaseId.length > 0;
  }
  if (typeof leaseId === 'number') {
    return !Number.isNaN(leaseId);
  }
  return false;
}

/**
 * Pull conflict payload rows out of a Fleet API error (`status.meta` CONFLICT entries and/or
 * top-level `data`). `conflictType` is the row discriminator (e.g. `'Lease'`).
 */
export function parseConflictDataFromHttpError(
  httpError: HttpErrorResponse,
  conflictType: string
): unknown[] {
  const body = httpError.error as
    | {
        data?: unknown;
        status?: { meta?: MetaModel<unknown>[] };
      }
    | undefined;

  const fromMeta: unknown[] = [];
  const meta = body?.status?.meta;
  if (Array.isArray(meta)) {
    for (const m of meta) {
      if (
        !isConflictDataWrapperType(m?.type) ||
        m.data === null ||
        m.data === undefined
      ) {
        continue;
      }
      appendConflictDataRows(fromMeta, asRowCandidates(m.data), conflictType);
    }
    if (fromMeta.length === 0) {
      for (const m of meta) {
        if (m?.data === null || m?.data === undefined) {
          continue;
        }
        appendConflictDataRows(fromMeta, asRowCandidates(m.data), conflictType);
      }
    }
  }

  const fromData: unknown[] = [];
  if (body?.data !== null && body?.data !== undefined) {
    appendConflictDataRows(fromData, asRowCandidates(body.data), conflictType);
  }

  const seenLeaseIds = new Set<string>();
  const out: unknown[] = [];
  for (const row of [...fromMeta, ...fromData]) {
    if (isLeaseConflictData(row)) {
      const id = String((row as LeaseConflictData).lease.leaseId);
      if (seenLeaseIds.has(id)) {
        continue;
      }
      seenLeaseIds.add(id);
    }
    out.push(row);
  }
  return out;
}

/**
 * All CONFLICT rows from the error body (`status.meta` and/or top-level `data`),
 * without filtering by row `type`. Lease-shaped rows are normalized like
 * {@link parseConflictDataFromHttpError}.
 */
export function parseAllConflictDataFromHttpError(
  httpError: HttpErrorResponse
): unknown[] {
  const body = httpError.error as
    | {
        data?: unknown;
        status?: { meta?: MetaModel<unknown>[] };
      }
    | undefined;

  const fromMeta: unknown[] = [];
  const meta = body?.status?.meta;
  if (Array.isArray(meta)) {
    for (const m of meta) {
      if (
        !isConflictDataWrapperType(m?.type) ||
        m.data === null ||
        m.data === undefined
      ) {
        continue;
      }
      appendAllConflictDataRows(fromMeta, asRowCandidates(m.data));
    }
    if (fromMeta.length === 0) {
      for (const m of meta) {
        if (m?.data === null || m?.data === undefined) {
          continue;
        }
        appendAllConflictDataRows(fromMeta, asRowCandidates(m.data));
      }
    }
  }

  const fromData: unknown[] = [];
  if (body?.data !== null && body?.data !== undefined) {
    appendAllConflictDataRows(fromData, asRowCandidates(body.data));
  }

  const seenLeaseIds = new Set<string>();
  const out: unknown[] = [];
  for (const row of [...fromMeta, ...fromData]) {
    if (isLeaseConflictData(row)) {
      const id = String((row as LeaseConflictData).lease.leaseId);
      if (seenLeaseIds.has(id)) {
        continue;
      }
      seenLeaseIds.add(id);
    }
    out.push(row);
  }
  return out;
}

/**
 * True when this 409 response includes at least one conflict row for `Lease`.
 * Delegates to {@link parseConflictDataFromHttpError} so checks stay aligned with the UI.
 */
export function hasConflictRecoverableMeta(
  httpErrorResponse: HttpErrorResponse
): boolean {
  if (httpErrorResponse.status !== 409) {
    return false;
  }
  return parseConflictDataFromHttpError(httpErrorResponse, 'Lease').length > 0;
}
